import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, MapPin, Send } from "lucide-react";
import http, { formatError } from "@/lib/api";

export default function Contact() {
    const [form, setForm] = useState({
        name: "", email: "", phone: "", subject: "general", message: "",
    });
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await http.post("/contact", form);
            toast.success("Message envoyé. Patrick vous répondra rapidement.");
            setForm({ name: "", email: "", phone: "", subject: "general", message: "" });
        } catch (err) {
            toast.error(formatError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <main data-testid="contact-page" className="pt-32 pb-24 min-h-screen">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="mb-20 max-w-3xl"
                >
                    <div className="overline mb-4">Contact</div>
                    <h1 className="serif text-5xl md:text-7xl leading-[0.95] mb-6">
                        Écrire à<br /><span className="italic-serif text-gilt">l'atelier</span>.
                    </h1>
                    <p className="text-bone-200 text-lg leading-relaxed">
                        Question, exposition, ou intéressé par un tableau —
                        Patrick lit chaque message.
                    </p>
                </motion.div>

                <div className="grid md:grid-cols-12 gap-12 md:gap-20">
                    {/* Sidebar */}
                    <aside className="md:col-span-4 space-y-10">
                        <div>
                            <div className="overline mb-3">Pour</div>
                            <ul className="space-y-3 text-bone-200">
                                <li className="flex gap-3"><span className="text-gilt serif">—</span> Se renseigner sur un tableau</li>
                                <li className="flex gap-3"><span className="text-gilt serif">—</span> Proposer un lieu d'exposition</li>
                                <li className="flex gap-3"><span className="text-gilt serif">—</span> Échanger entre artistes</li>
                            </ul>
                        </div>

                        <div>
                            <div className="overline mb-3">Coordonnées</div>
                            <ul className="space-y-3 text-bone-200">
                                <li className="flex items-center gap-3 text-sm">
                                    <Mail size={14} className="text-gilt" />
                                    contact@patrickmathurin.art
                                </li>
                                <li className="flex items-center gap-3 text-sm">
                                    <MapPin size={14} className="text-gilt" />
                                    Allier (03) · France
                                </li>
                            </ul>
                        </div>

                        <div className="border-l border-gilt pl-6">
                            <p className="italic-serif text-xl text-bone-100 leading-relaxed">
                                « Merci de votre visite, vous pouvez me laisser un message,
                                cela me fera plaisir. »
                            </p>
                            <p className="overline mt-4">Patrick Mathurin</p>
                        </div>
                    </aside>

                    {/* Form */}
                    <form onSubmit={submit} className="md:col-span-8 space-y-8" data-testid="contact-form">
                        <div className="grid md:grid-cols-2 gap-8">
                            <input
                                data-testid="contact-name"
                                type="text"
                                placeholder="Nom & prénom"
                                className="input-line"
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                required
                            />
                            <input
                                data-testid="contact-email"
                                type="email"
                                placeholder="Email"
                                className="input-line"
                                value={form.email}
                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                                required
                            />
                        </div>
                        <input
                            data-testid="contact-phone"
                            type="tel"
                            placeholder="Téléphone (optionnel)"
                            className="input-line"
                            value={form.phone}
                            onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        />

                        <div>
                            <label className="overline block mb-4">Sujet</label>
                            <div className="flex flex-wrap gap-3">
                                {[
                                    { v: "general", l: "Message général" },
                                    { v: "exposition", l: "Exposition / partenariat" },
                                ].map((s) => (
                                    <button
                                        key={s.v}
                                        type="button"
                                        data-testid={`subject-${s.v}`}
                                        onClick={() => setForm({ ...form, subject: s.v })}
                                        className={`px-4 py-2 border text-xs uppercase tracking-[0.18em] transition-colors ${form.subject === s.v
                                            ? "border-gilt text-gilt"
                                            : "border-white/15 text-bone-300 hover:border-bone-300"
                                            }`}
                                    >
                                        {s.l}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <textarea
                            data-testid="contact-message"
                            placeholder="Votre message…"
                            rows={6}
                            className="input-line resize-none"
                            value={form.message}
                            onChange={(e) => setForm({ ...form, message: e.target.value })}
                            required
                        />

                        <div className="flex items-center justify-between pt-4">
                            <p className="text-xs text-bone-400 max-w-xs">
                                En envoyant ce formulaire, vous acceptez d'être recontacté par l'atelier.
                            </p>
                            <button data-testid="contact-submit" type="submit" className="btn-gilt" disabled={busy}>
                                {busy ? "Envoi…" : "Envoyer"} <Send size={13} />
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </main>
    );
}
